import { hlsFetchSetup } from '@lib/hls/fetchSetup';
import { isHlsSupported } from '@lib/hls/support';
import { useMediaFilePreview } from '@lib/hooks/preview/preview';
import { getThumbnailFromVideo } from '@lib/image/fromVideo';
import { type OriginalUnavailableReason, useOriginalMediaUrl } from '@lib/media/originalMedia';
import { HLS_PLAYLIST_PATH } from '@lib/network/preview';
import { logarithmicToLinear } from '@lib/sound/logarithmicToLinear';
import { MB_TO_BYTES } from '@lib/utils/units';
import { libraryPreview } from '@stores/Library/LibraryPreview';
import { Clip } from '@stores/MomentMerge/MomentMergeStore';
import Hls from 'hls.js';
import React, { useCallback, useEffect, useRef } from 'react';
import css from '../MomentMergeVideoPlayer.module.scss';
import { ClipStore, PlaybackSource } from '../types';

/**
 * How far `currentTime` must move for a `seeked` event to be worth waiting on. Below one frame
 * at 60fps (16.7ms), so anything under it is a position the element is already showing.
 */
const SEEK_EPSILON_SECONDS = 0.01;

/**
 * VideoComponent handles video playback for individual clips.
 *
 * It plays a clip from one of two sources — the transcoded HLS preview or the original file the
 * user uploaded (see `PlaybackSource`) — onto a single `<video>` element. Which one is attached
 * is tracked in `attachedRef`, and switching between them keeps the viewer's position.
 *
 * This component uses a key prop (set in VideoWrapper) built from the clip ID, so a different
 * clip mounts a fresh instance rather than mutating this one. This replaces the fragile
 * "set-to-null-and-back" pattern that was previously used to force re-renders during seeks.
 */
interface VideoComponentProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  hlsRef: React.MutableRefObject<Hls | null>;
  isActive: boolean;
  isPaused: boolean;
  clipData: Clip | null;
  currentClipIndex: number;
  onTimeUpdate: () => void;
  onEnded: () => void;
  onReady: () => void;
  onLoadingChange: (loading: boolean) => void;
  store: ClipStore;
  onClick?: () => void;
  targetTime?: number;
  activeMomentId?: string | null;
  /** Which rendition to play. Defaults to the transcoded preview; see `PlaybackSource`. */
  playbackSource?: PlaybackSource;
  /**
   * Called when `playbackSource` is `'original'` but the file cannot be played — its URL did
   * not resolve, or the browser could not decode the container the user uploaded. The owner of
   * the selection is expected to fall back to the preview and say so; this component keeps
   * showing whatever is still attached.
   */
  onOriginalUnavailable?: (reason: OriginalUnavailableReason) => void;
}

export const VideoComponent: React.FC<VideoComponentProps> = ({
  videoRef,
  hlsRef,
  isActive,
  isPaused,
  clipData,
  currentClipIndex,
  onTimeUpdate,
  onEnded,
  onReady,
  onLoadingChange,
  store,
  onClick,
  targetTime,
  activeMomentId,
  playbackSource = 'preview',
  onOriginalUnavailable,
}) => {
  const { url: previewUrl, token: previewToken } = useMediaFilePreview(
    clipData?.libraryId,
    clipData?.mediaId,
    HLS_PLAYLIST_PATH,
  );

  const { url: originalUrl, error: originalUrlError } = useOriginalMediaUrl(
    clipData?.libraryId,
    clipData?.mediaId,
    playbackSource === 'original',
  );

  // Whichever URL the selected source plays from. Null while it is still resolving, which is
  // what keeps the previously attached source running instead of blanking the element.
  const sourceUrl = playbackSource === 'original' ? originalUrl : previewUrl;

  useEffect(() => {
    // Never a format verdict: this fires before the element has seen a byte of the file.
    if (originalUrlError) onOriginalUnavailable?.('load-failed');
  }, [originalUrlError, onOriginalUnavailable]);

  /**
   * Set when a switch to the original started resolving but never reached the element — the
   * resolution failed, or the viewer chose Preview again while it was still in flight.
   *
   * Nothing about the element changed in that case, so `attachedRef` still reads `'preview'`
   * and the attach effect would take the return to Preview for a no-op, leaving the spinner
   * that switch turned on with nothing to clear it. Reading it as a real switch instead hands
   * the spinner back to the attach path, which owns it through to `canplay`.
   */
  const originalSwitchAbandonedRef = useRef(false);
  /**
   * Whether this component is holding the slot's spinner for an original that is still
   * resolving — as opposed to an attach, which holds it through to `canplay`. Only a spinner
   * this effect turned on is this effect's to account for.
   */
  const resolveSpinnerHeldRef = useRef(false);

  // Resolving the original's URL costs a round trip before the element is handed anything, and
  // the preview keeps playing meanwhile — so without this the menu closes and nothing whatsoever
  // acknowledges the click. The attach that follows sets it again; `canplay` clears both.
  const isResolvingOriginal = playbackSource === 'original' && !originalUrl && !originalUrlError;
  useEffect(() => {
    if (isResolvingOriginal && isActive) {
      onLoadingChange(true);
      resolveSpinnerHeldRef.current = true;
      return;
    }
    if (!resolveSpinnerHeldRef.current) return;
    resolveSpinnerHeldRef.current = false;
    // The spinner turned on above is still up. With a URL in hand the attach effect takes it
    // over in this same commit; with none, nothing is going to attach, so record the abandoned
    // attempt and let the return to Preview re-attach and put the spinner back under `canplay`.
    if (!originalUrl) originalSwitchAbandonedRef.current = true;
  }, [isResolvingOriginal, originalUrl, isActive, onLoadingChange]);

  // Refs to track event listeners for cleanup
  const pendingListenersRef = useRef<
    {
      event: string;
      handler: EventListener;
    }[]
  >([]);

  // Helper to add a tracked event listener
  const addTrackedListener = useCallback(
    (video: HTMLVideoElement, event: string, handler: EventListener) => {
      video.addEventListener(event, handler);
      pendingListenersRef.current.push({ event, handler });
    },
    [],
  );

  // Helper to clean up all tracked listeners
  const cleanupTrackedListeners = useCallback(() => {
    const video = videoRef.current;
    if (video && pendingListenersRef.current.length > 0) {
      pendingListenersRef.current.forEach(({ event, handler }) => {
        video.removeEventListener(event, handler);
      });
      pendingListenersRef.current = [];
    }
  }, [videoRef]);

  /**
   * Attaches the selected source to the `<video>` element and starts it at `startAt` (absolute
   * media seconds). Both sources share one element, so the caller detaches the previous one
   * first — see `detachCurrentSource`.
   */
  const initializeSource = useCallback(
    (startAt: number) => {
      if (!clipData) return;

      const video = videoRef.current;
      if (!video) return;

      // Clean up any pending listeners from previous initialization
      cleanupTrackedListeners();

      /**
       * Positions the freshly attached source and, when this clip is the one playing, resumes it.
       *
       * Waiting for `seeked` before playing is what keeps the first frames from coming out of the
       * old position — but assigning `currentTime` the value it already holds fires no `seeked` at
       * all, so waiting on one would strand playback forever. That happens whenever a source is
       * attached at the position the element is already at, which is every attach at 0: a quality
       * switch before the viewer has played anything, or a switch to full-media mode at the start.
       */
      const startPlaybackAt = () => {
        const seekPending = Math.abs(video.currentTime - startAt) > SEEK_EPSILON_SECONDS;
        video.currentTime = startAt;
        video.muted = !isActive;

        if (!isActive || isPaused) return;

        if (!seekPending) {
          video.play().catch(console.error);
          return;
        }

        const handleSeeked = () => {
          video.removeEventListener('seeked', handleSeeked);
          // Remove from tracked listeners since we're cleaning up manually
          pendingListenersRef.current = pendingListenersRef.current.filter(
            (listener) => listener.handler !== handleSeeked,
          );
          video.play().catch(console.error);
        };
        addTrackedListener(video, 'seeked', handleSeeked);
      };

      /**
       * Plays a URL the element can fetch on its own: the presigned original, and the native-HLS
       * playlist below. Neither needs a request hook, because neither authenticates per segment.
       */
      const attachDirectSource = (url: string) => {
        // hls.js buffers as soon as it attaches, so the preview reaches a painted frame whether or
        // not anyone presses play. A bare element does not: left at the default `metadata` it
        // stops at `HAVE_METADATA` — no frame, and no `canplay`, so the spinner never clears — and
        // only a seek would wake it, which an attach at position 0 does not perform.
        video.preload = 'auto';
        video.src = url;
        video.load(); // Trigger reload with the new src

        // Set up event listener for when video is ready
        const handleLoadedMetadata = () => {
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
          // Remove from tracked listeners since we're cleaning up manually
          pendingListenersRef.current = pendingListenersRef.current.filter(
            (listener) => listener.handler !== handleLoadedMetadata,
          );
          startPlaybackAt();
        };
        addTrackedListener(video, 'loadedmetadata', handleLoadedMetadata);
      };

      if (playbackSource === 'original') {
        if (!originalUrl) return;
        attachDirectSource(originalUrl);
        return;
      }

      // `previewUrl` is set only once `previewToken` is non-null (useMediaFilePreview), so
      // one guard covers both, and returning silently is the normal state while the token
      // resolves. Known gap: a token fetch that fails for good leaves this returning
      // forever — a blank frame, no spinner, no retry. `usePreviewToken` logs it, but
      // surfacing it to the user needs an error state on the shared hook, which four
      // other consumers share.
      if (!previewUrl || !previewToken) return;

      // Use HLS.js when supported
      const useHlsJs = isHlsSupported();
      if (useHlsJs) {
        const hls = new Hls({
          // A getter, not the captured string: a merge session can outlive a token, and
          // this runs per playlist/segment fetch, so a refresh by any consumer (see
          // `usePreviewToken`) is picked up without rebuilding the player. Falling back
          // to the token this instance was built with is no worse than a static one.
          // hlsFetchSetup's Request sets no `credentials`, so it defaults to
          // `same-origin` and sends none to the cross-origin preview host.
          fetchSetup: hlsFetchSetup(
            () => libraryPreview.getCachedPreviewToken(clipData.libraryId) ?? previewToken,
          ),
          // Load-bearing for AUTH, not performance — do not remove. See the warning on
          // `hlsFetchSetup`, which explains why.
          progressive: true,
          // Required whenever `progressive` is on, not an optimisation. Left unset, hls.js
          // loads from 0 and `startPlaybackAt`'s seek cancels that fragment mid-stream — and a
          // progressive fragment has already appended partial chunks by then, so the next
          // append meets a jagged buffer and MSE rejects it (Chrome:
          // CHUNK_DEMUXER_ERROR_APPEND_FAILED). Every clip starting past 0 hits it. See
          // `.claude/docs/video-playback-sources.md`.
          startPosition: startAt,
          maxBufferLength: 30,
          maxMaxBufferLength: 60,
          maxBufferSize: 60 * MB_TO_BYTES,
          maxBufferHole: 0.1, // Smaller buffer hole tolerance
          lowLatencyMode: false,
          backBufferLength: 90,
          // Additional buffer management options
          maxStarvationDelay: 2000,
          // Network and loading options
          manifestLoadingTimeOut: 10000,
          manifestLoadingMaxRetry: 3,
          levelLoadingTimeOut: 10000,
          levelLoadingMaxRetry: 3,
          fragLoadingTimeOut: 20000,
          fragLoadingMaxRetry: 3,
        });

        hlsRef.current = hls;

        hls.on(Hls.Events.MANIFEST_PARSED, () => startPlaybackAt());

        let authRetried = false;

        hls.on(Hls.Events.ERROR, (event, data) => {
          // Enhanced error logging for VideoComponent
          console.error('HLS error in VideoComponent:', {
            type: data.type,
            details: data.details,
            fatal: data.fatal,
            url: data.url,
            response: data.response,
            error: data.error,
            event: event,
            clipId: clipData.id,
            mediaId: clipData.mediaId,
            videoCurrentTime: video?.currentTime,
            videoReadyState: video?.readyState,
            videoNetworkState: video?.networkState,
            bufferInfo: video
              ? {
                  buffered: Array.from({ length: video.buffered.length }, (_, i) => ({
                    start: video.buffered.start(i),
                    end: video.buffered.end(i),
                  })),
                  duration: video.duration,
                }
              : null,
          });

          if (data.fatal) {
            const is401 = data.type === Hls.ErrorTypes.NETWORK_ERROR && data.response?.code === 401;
            if (is401 && !authRetried) {
              authRetried = true;
              // The server rejected a token the cache still considers valid, so a plain
              // cache read cannot get past it. Refresh, then resume: `fetchSetup` re-reads
              // the cache per request, so `startLoad` retries with the new token and picks
              // up where it stopped instead of rebuilding the player.
              libraryPreview
                .refreshPreviewToken(clipData.libraryId)
                .then(() => {
                  if (hlsRef.current === hls) hls.startLoad();
                })
                .catch((error) => {
                  console.error('Preview token refresh failed for clip:', clipData.mediaId, error);
                  onLoadingChange(false);
                });
              return;
            }

            // A fatal media error ends this attach. Not `hls.recoverMediaError()`: its
            // re-attach restarts loading at `config.startPosition`, which is pinned above, so
            // recovery would silently throw the viewer back to where the clip was attached —
            // and it resumes buffering without resuming playback, because nothing re-fires
            // `MANIFEST_PARSED` and `startPlaybackAt` is the only thing that calls `play()`.
            console.error('Fatal HLS error for clip:', clipData.mediaId);
            onLoadingChange(false);
          }
        });

        video.preload = 'metadata';
        hls.loadSource(previewUrl);
        hls.attachMedia(video);
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        // Native HLS fallback (rarely used - only old Safari/iOS that don't support HLS.js)
        // The token is baked into the URL, so unlike the HLS.js path above this one does
        // NOT rotate: an expired token here needs a new src plus load(), i.e. a playback
        // restart. Left as-is because the path predates the preview host and is unreachable
        // on any browser with MSE or ManagedMediaSource.
        const separator = previewUrl.includes('?') ? '&' : '?';
        attachDirectSource(
          `${previewUrl}${separator}access_token=${encodeURIComponent(previewToken)}`,
        );
      } else {
        console.error('HLS not supported');
        onLoadingChange(false);
      }
    },
    [
      clipData,
      playbackSource,
      originalUrl,
      previewUrl,
      previewToken,
      isActive,
      isPaused,
      onLoadingChange,
      addTrackedListener,
      cleanupTrackedListeners,
      videoRef,
      hlsRef,
    ],
  );

  /**
   * Releases whichever source is attached. Both live on the same element, so a switch that
   * skipped this would leave the outgoing one buffering behind the incoming one.
   */
  const detachCurrentSource = useCallback(() => {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const video = videoRef.current;
    if (video?.src) {
      video.removeAttribute('src');
      video.load();
    }
  }, [hlsRef, videoRef]);

  // Add protection against race conditions
  const isInitializingRef = useRef(false);
  /** Which clip, from which source, is on the element right now — null until the first attach. */
  const attachedRef = useRef<{ clipId: string; source: PlaybackSource } | null>(null);
  const currentInitializationIdRef = useRef(0);
  const lastTargetTimeRef = useRef<number | undefined>(undefined);

  // Handle source initialization and cleanup
  useEffect(() => {
    // Bail before touching `isInitializingRef` while the source URL is still resolving: the
    // flag is only cleared by a timeout tied to the latest initialization id, so incrementing
    // it here and returning early would leave the flag stuck and block the real init.
    if (!clipData || !sourceUrl) return;

    const video = videoRef.current;
    if (!video) return;

    // Generate unique ID for this initialization
    const initializationId = ++currentInitializationIdRef.current;

    const attached = attachedRef.current;
    const isSameClip = attached?.clipId === clipData.id;
    // An abandoned switch counts: the element still holds the preview, but the spinner is on and
    // only a re-attach puts it back under `canplay`.
    const isQualitySwitch =
      !!attached &&
      isSameClip &&
      (attached.source !== playbackSource || originalSwitchAbandonedRef.current);

    // Prevent multiple simultaneous attach operations. A quality switch is exempt: it is a
    // deliberate, one-off click, and dropping it would leave the menu claiming a source that
    // is not playing, with nothing to trigger a retry. Re-entering is safe because the attach
    // path tears the previous source down first.
    if (isInitializingRef.current && !isQualitySwitch) return;

    // Re-attach for a different clip, or for the same clip played from the other source
    if (!isSameClip || isQualitySwitch) {
      isInitializingRef.current = true;
      onLoadingChange(true);

      // A quality switch resumes where the viewer had got to; both sources are timed against
      // the same media, so the position carries over as-is. A clip change starts from the clip.
      const startAt =
        isSameClip && video.currentTime > 0
          ? video.currentTime
          : (targetTime ?? clipData.startTime);

      detachCurrentSource();
      initializeSource(startAt);
      attachedRef.current = { clipId: clipData.id, source: playbackSource };
      originalSwitchAbandonedRef.current = false;
      lastTargetTimeRef.current = targetTime;
    } else {
      // Same clip, just update the time and state
      // Only set currentTime if targetTime actually changed, not just pause state
      const targetTimeChanged = lastTargetTimeRef.current !== targetTime;
      if (targetTimeChanged) {
        video.currentTime = targetTime || clipData.startTime;
        lastTargetTimeRef.current = targetTime;
      }
      video.muted = !isActive;

      if (isActive && !isPaused && video.paused) {
        video.play().catch(console.error);
      }
    }

    // Reset the flag after a short delay to allow for async operations
    setTimeout(() => {
      // Only reset if this is still the latest initialization
      if (initializationId === currentInitializationIdRef.current) {
        isInitializingRef.current = false;
      }
    }, 100);

    return () => {
      // Clean up any pending event listeners
      cleanupTrackedListeners();

      // Only destroy HLS on unmount, not on clip changes
      if (hlsRef.current && !clipData) {
        hlsRef.current.destroy();
        hlsRef.current = null;
        attachedRef.current = null;
      }
    };
  }, [
    clipData?.id,
    sourceUrl,
    playbackSource,
    initializeSource,
    detachCurrentSource,
    targetTime,
    isActive,
    isPaused,
    cleanupTrackedListeners,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCanPlay = () => {
      onReady();
      onLoadingChange(false);
    };

    const handleCanPlayThrough = () => {
      onLoadingChange(false);
    };

    const handleLoadedData = () => {
      setTimeout(() => {
        if (store.preview === null) {
          store.setPreview(getThumbnailFromVideo(video));
        }
      }, 100);
    };

    const handleTimeUpdate = () => {
      onTimeUpdate();
    };

    const handleEnded = () => {
      onEnded();
    };

    const handleLoadStart = () => {
      onLoadingChange(true);
    };

    const handleError = () => {
      console.error('Video error for clip:', clipData?.mediaId, video.error);
      onLoadingChange(false);

      // Nothing transcodes the original, so the browser meets whatever container and codec the
      // user uploaded, on a presigned URL that will eventually expire. Either way this source
      // is done — hand the decision back rather than sitting on a dead element, and say which
      // of the two it was: only the format verdict is a statement about the file itself.
      //
      // MEDIA_ERR_DECODE joins MEDIA_ERR_SRC_NOT_SUPPORTED because both end at the same
      // sentence for the user — the browser opened the file and could not make pictures of it.
      // MEDIA_ERR_NETWORK, MEDIA_ERR_ABORTED and a missing error object are all "we never got
      // the bytes", which is the retryable one.
      if (attachedRef.current?.source === 'original') {
        const code = video.error?.code;
        const isFormatError =
          code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED || code === MediaError.MEDIA_ERR_DECODE;
        onOriginalUnavailable?.(isFormatError ? 'unsupported-format' : 'load-failed');
      }
    };

    const handleStalled = () => {
      console.warn('Video stalled for clip:', clipData?.mediaId);
      // Don't set loading to false on stall, as it might recover
    };

    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('canplaythrough', handleCanPlayThrough);
    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('error', handleError);
    video.addEventListener('stalled', handleStalled);

    return () => {
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('canplaythrough', handleCanPlayThrough);
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('error', handleError);
      video.removeEventListener('stalled', handleStalled);
    };
  }, [
    onTimeUpdate,
    onEnded,
    clipData,
    isActive,
    onReady,
    onLoadingChange,
    onOriginalUnavailable,
    store,
  ]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !isActive;
  }, [isActive]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !isActive) return;

    video.muted = store.isMuted;

    if (!store.isMuted) {
      const logarithmicVolume = logarithmicToLinear(store.volume) / 100;
      video.volume = logarithmicVolume;
    }
  }, [isActive, store.isMuted, store.volume]);

  // Cleanup effect to reset loading state when clip changes or component unmounts
  useEffect(() => {
    return () => {
      // Reset loading state when component unmounts or clip changes
      onLoadingChange(false);
    };
  }, [clipData?.id, onLoadingChange]);

  return (
    <video
      ref={videoRef}
      className={`${css.videoElement} ${activeMomentId && clipData && activeMomentId === clipData.id ? css.videoElementActive : ''}`}
      style={{
        opacity: isActive ? 1.0 : 0,
        zIndex: isActive ? 2 : 1,
      }}
      crossOrigin="anonymous"
      playsInline
      onClick={onClick}
    />
  );
};
